import { useEffect, useState } from "react"
import { X } from "lucide-react"
import { cn } from "../../lib/utils"
import { Button } from "../../components/button"
import { AgentChat } from "./agent-chat"
import { useIsMobile } from "../../hooks/use-mobile"
import type { AgentChatProps } from "./types"

/** How long the panel takes to slide out before unmounting. Match
 * to the CSS transition duration on the panel + aura. */
const EXIT_DURATION_MS = 150

export interface ChatPanelProps extends AgentChatProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Width of the panel in px on desktop. Default 420. The host
   * pages itself in by `padding-right` so its content slides left
   * to make room. */
  width?: number
  /** Inset (px) from each viewport edge. The panel floats with
   * this margin all around so it reads as part of the AI surface
   * family rather than a docked sheet. Default 12. */
  inset?: number
  /** The drifting radial-gradient background behind the frosted glass.
   * Default true. Set false for hosts that want a flatter, static dock
   * (e.g. a page-level side panel rather than a floating AI surface) —
   * the frosted blur body itself is unaffected, only the moving aura
   * layer behind it is skipped. */
  showAura?: boolean
  /** The panel body's translucent, backdrop-blurred glass background.
   * Default true. Set false for a plain solid `bg-background` body —
   * pairs with `showAura={false}` for a flat dock that reads as part of
   * the app chrome rather than a floating AI surface. */
  frosted?: boolean
}

/**
 * Inline floating chat panel — the AI surface's max-mode on
 * desktop. Same frosted-glass aesthetic as `<ChatPill>`'s small-
 * mode message overlay so the two states feel continuous.
 *
 * Two stacked layers:
 *   - Aura — radial gradients painted in the inverted theme bg
 *     colour. Soft fields of colour the user almost-sees behind
 *     the glass.
 *   - Panel — translucent body (`bg-background/30`), heavy
 *     backdrop-blur. Sits on top of the aura; the blur turns its
 *     distinct shapes into cloudy colour.
 *
 * Mobile renders nothing — `<ChatPill>`'s "tall" mode is the
 * mobile expand path.
 */
export function ChatPanel({
  open,
  onOpenChange,
  width = 420,
  inset = 12,
  showAura = true,
  frosted = true,
  className,
  ...chatProps
}: ChatPanelProps) {
  const isMobile = useIsMobile()

  // Delayed-unmount so the closing transition (opacity + slide) has
  // time to play before the DOM nodes go away. `mounted` controls
  // whether we render at all; `visible` flips the open-state CSS
  // classes on. On open: mount immediately, then on the next frame
  // flip visible=true so the transition runs from the closed state.
  const [mounted, setMounted] = useState(open)
  const [visible, setVisible] = useState(open)
  useEffect(() => {
    if (open) {
      setMounted(true)
      // requestAnimationFrame so the browser actually paints the
      // initial closed state before transitioning to open.
      const raf = requestAnimationFrame(() => setVisible(true))
      return () => cancelAnimationFrame(raf)
    } else {
      setVisible(false)
      const t = setTimeout(() => setMounted(false), EXIT_DURATION_MS)
      return () => clearTimeout(t)
    }
  }, [open])

  if (isMobile || !mounted) return null

  const boundingBox: React.CSSProperties = {
    width,
    top: inset,
    right: inset,
    bottom: inset,
  }

  return (
    <>
      {/* ── Aura ──────────────────────────────────────────────
          Three soft radial blobs of the OPPOSITE theme's bg
          colour (light mode → dark green; dark mode → cream).
          Aura colour swaps via a CSS variable flipped by the
          standard Tailwind `dark:` variant — no JS dark-mode
          hook needed. The inline `background:` consumes the
          var. Opt out via `showAura={false}` for a flatter,
          static dock. */}
      {showAura && (
        <div
          data-slot="chat-panel-aura"
          aria-hidden="true"
          className={cn(
            "pointer-events-none fixed z-30 overflow-hidden rounded-3xl",
            "[--aura-color:#1a1f19] dark:[--aura-color:#f7f1e7]",
            "transition-[opacity,transform] duration-150 ease-in-out",
            // Resting opacity (was inline `opacity: 0.6` — but inline
            // style beats Tailwind class specificity, which meant the
            // `opacity-0` exit class got ignored and the aura just
            // hung at 0.6 until unmount. As a class it composes with
            // the conditional `opacity-0` properly.
            "opacity-50",
            !visible && "!opacity-0 translate-x-6",
          )}
          style={{
            ...boundingBox,
            background: `
              radial-gradient(50% 40% at 72% 24%, var(--aura-color) 0%, transparent 65%),
              radial-gradient(46% 38% at 24% 76%, var(--aura-color) 0%, transparent 62%),
              radial-gradient(58% 46% at 50% 55%, var(--aura-color) 0%, transparent 70%),
              radial-gradient(75% 32% at 50% 100%, var(--aura-color) 0%, transparent 75%)
            `,
          }}
        >
          <span
            aria-hidden="true"
            className="agent-chat-panel-aura-drift absolute inset-0"
          />
        </div>
      )}

      {/* ── Frosted panel ─────────────────────────────────────
          Pure Tailwind: translucent token-driven bg + backdrop-
          blur. Same `bg-background/N` pattern the pill uses for
          its small-mode overlay so the two surfaces match. */}
      <aside
        data-slot="chat-panel"
        aria-label="Agent chat"
        className={cn(
          "agent-chat-root new-ui-theme fixed z-30 isolate flex flex-col overflow-hidden rounded-3xl",
          "ring-1 ring-black/10 dark:ring-white/10",
          "shadow-[0_24px_60px_-20px_rgba(0,0,0,0.35)]",
          frosted
            ? "bg-background/30 supports-backdrop-filter:bg-background/15 supports-backdrop-filter:backdrop-blur-2xl supports-backdrop-filter:backdrop-saturate-150"
            : "bg-background",
          "transition-[opacity,transform] duration-150 ease-in-out",
          !visible && "translate-x-6 opacity-0",
          className,
        )}
        style={boundingBox}
      >
        {/* Top region — just an alignment container for the
            close button. No bg, no blur on the bar itself, so
            scrolled messages read clearly behind. The button
            keeps its own subtle frosted chip so it stays
            clickable against any colour underneath. */}
        <div className="pointer-events-none absolute inset-x-0 top-0 z-20 flex h-12 items-center justify-end px-3">
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            onClick={() => onOpenChange(false)}
            aria-label="Close chat"
            className="pointer-events-auto bg-background/50 backdrop-blur-md hover:bg-background/80"
          >
            <X />
          </Button>
        </div>

        {/* `!bg-transparent` overrides AgentChat's root
            `bg-background` (fully opaque — would otherwise paint
            over our frost + aura). The composer keeps its own
            bg as a card; that's intentional, gives the input
            chrome against the frosted body. */}
        <AgentChat
          {...chatProps}
          className="relative z-10 h-full !bg-transparent"
          hideHeader
        />
      </aside>

      {/* Drift keyframes for the aura. Inlined so the block stays
          self-contained — no host-side CSS import required. */}
      {showAura && (
        <style>{`
          @keyframes agent-chat-panel-aura-drift {
            0%   { transform: translate3d(0, 0, 0) scale(1); }
            25%  { transform: translate3d(-22%, 8%, 0) scale(1.12); }
            50%  { transform: translate3d(-10%, 22%, 0) scale(1.18); }
            75%  { transform: translate3d(14%, 14%, 0) scale(1.12); }
            100% { transform: translate3d(0, 0, 0) scale(1); }
          }
          .agent-chat-panel-aura-drift {
            background: inherit;
            animation: agent-chat-panel-aura-drift 12s ease-in-out infinite;
          }
          @media (prefers-reduced-motion: reduce) {
            .agent-chat-panel-aura-drift { animation: none; }
          }
        `}</style>
      )}
    </>
  )
}
